import React from 'react';
import { Box, AppBar, Toolbar, Typography, Drawer, List, ListItemButton, ListItemIcon, ListItemText, IconButton, useTheme, Tooltip, Collapse } from '@mui/material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import SportsCricketIcon from '@mui/icons-material/SportsCricket';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import PeopleIcon from '@mui/icons-material/People';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FlagIcon from '@mui/icons-material/Flag';
import MenuIcon from '@mui/icons-material/Menu';
import FolderIcon from '@mui/icons-material/Folder';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import DashboardIcon from '@mui/icons-material/Dashboard';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { Build as BuildIcon } from '@mui/icons-material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import SportsIcon from '@mui/icons-material/Sports';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { logout, getUser } from '../../services/auth';
import { ThemeContext } from '../../App';
import ArticleIcon from '@mui/icons-material/Article';
import PagesIcon from '@mui/icons-material/Pages';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import ChatIcon from '@mui/icons-material/Chat';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ApiEndpointSelector from '../ApiEndpointSelector/ApiEndpointSelector';

const drawerWidth = 240;

interface MenuItemWithPath {
  text: string;
  icon: React.ReactElement;
  path: string;
}

interface MenuItemWithSubItems {
  text: string;
  icon: React.ReactElement;
  subItems: MenuItemWithPath[];
}

type MenuItem = MenuItemWithPath | MenuItemWithSubItems;

function hasSubItems(item: MenuItem): item is MenuItemWithSubItems {
  return 'subItems' in item;
}

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const theme = useTheme();
  const themeContext = React.useContext(ThemeContext);
  const [openMenus, setOpenMenus] = React.useState<{ [key: string]: boolean }>({
    Marketplace: location.pathname.startsWith('/marketplace'),
  });
  const user = getUser();
  const roles: string[] = user?.roles || [];
  const isContentEditor = roles.includes('content_editor') && !user?.is_superuser;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const handleThemeToggle = () => {
    themeContext.toggleColorMode();
  };

  const handleMenuClick = (text: string) => {
    setOpenMenus((prevOpenMenus) => ({
      ...prevOpenMenus,
      [text]: !prevOpenMenus[text],
    }));
  };

  const baseMenu: MenuItem[] = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Content Dashboard', icon: <DashboardIcon />, path: '/content-dashboard' },
    {
      text: 'Marketplace',
      icon: <StorefrontIcon />,
      subItems: [
        { text: 'Dashboard', icon: <StorefrontIcon />, path: '/marketplace' },
        { text: 'Churned users', icon: <PersonOffIcon />, path: '/marketplace/churn' },
      ],
    },
    { text: 'User Management', icon: <SupervisorAccountIcon />, path: '/users' },
    { text: 'Support Chat', icon: <ChatIcon />, path: '/support' },
    {
      text: 'CMS',
      icon: <ArticleIcon />,
      subItems: [
        { text: 'Articles', icon: <ArticleIcon />, path: '/cms/articles' },
        { text: 'Pages', icon: <PagesIcon />, path: '/cms/pages' },
        { text: 'Templates', icon: <PagesIcon />, path: '/cms/templates' },
        { text: 'Menu', icon: <MenuIcon />, path: '/menu-management' },
        { text: 'AI Content Editor', icon: <AutoAwesomeIcon />, path: '/delphi' },
        { text: 'AI Prompt Library', icon: <AutoAwesomeIcon />, path: '/delphi/prompts' },
      ],
    },
    {
      text: 'Cricket',
      icon: <SportsCricketIcon />,
      subItems: [
        { text: 'Series', icon: <EmojiEventsIcon />, path: '/series?status=ONGOING' },
        { text: 'Matches', icon: <SportsCricketIcon />, path: '/matches?status=Not%20Started' },
        { text: 'Players', icon: <PeopleIcon />, path: '/players' },
        { text: 'Match Players', icon: <PersonIcon />, path: '/match-players' },
        { text: 'Squad Players', icon: <GroupIcon />, path: '/squad-players' },
        { text: 'Teams', icon: <FlagIcon />, path: '/teams' },
        { text: 'Grounds', icon: <LocationOnIcon />, path: '/grounds' },
        { text: 'DT Analysis', icon: <DashboardIcon />, path: '/dream-team-analysis' },
      ],
    },
    {
      text: 'Football',
      icon: <SportsIcon />,
      subItems: [
        { text: 'Leagues', icon: <EmojiEventsIcon />, path: '/football/leagues' },
        { text: 'Matches', icon: <SportsIcon />, path: '/football/matches' },
        { text: 'Players', icon: <PeopleIcon />, path: '/football/players' },
        { text: 'Match Players', icon: <PersonIcon />, path: '/football/match-players' },
        { text: 'Squad Players', icon: <GroupIcon />, path: '/football/squad-players' },
        { text: 'Teams', icon: <FlagIcon />, path: '/football/teams' },
        { text: 'Stadiums', icon: <LocationOnIcon />, path: '/football/stadiums' },
        { text: 'Referees', icon: <SupervisorAccountIcon />, path: '/football/referees' },
        { text: 'Events', icon: <BuildIcon />, path: '/football/events' },
      ],
    },
    {
      text: 'Fantasy',
      icon: <EmojiEventsIcon />,
      subItems: [
        { text: 'Match Player Groups', icon: <SettingsIcon />, path: '/match-player-group-configs' },
      ],
    },
    { text: 'Gallery', icon: <FolderIcon />, path: '/media' },
    { text: 'Connect', icon: <NotificationsIcon />, path: '/connect' },
    {
      text: 'Xtract',
      icon: <BuildIcon />,
      subItems: [
        { text: 'Sport Events', icon: <SportsIcon />, path: '/sport-events' },
        { text: 'Tournaments', icon: <EmojiEventsIcon />, path: '/tournaments' },
        { text: 'Manual Actions', icon: <BuildIcon />, path: '/manual-actions' },
      ],
    },
    { text: 'Expo Updates', icon: <PhoneIphoneIcon />, path: '/expo-updates' },
    { text: 'Install Attribution', icon: <AnalyticsIcon />, path: '/app-attribution' },
  ];

  const contentAllowedPrefixes = ['/cms', '/menu-management', '/media', '/delphi', '/content-dashboard'];
  const menuItems: MenuItem[] = isContentEditor
    ? baseMenu
        .map((item): MenuItem | null => {
          if (hasSubItems(item)) {
            const filteredSub = item.subItems.filter((s) =>
              contentAllowedPrefixes.some((p) => s.path.startsWith(p))
            );
            if (filteredSub.length) {
              return { ...item, subItems: filteredSub };
            }
            return null;
          }
          if (contentAllowedPrefixes.some((p) => item.path.startsWith(p)) || item.path === '/') {
            return item;
          }
          return null;
        })
        .filter((item): item is MenuItem => item !== null)
    : baseMenu;

  const renderMenuItem = (item: MenuItem) => {
    if (hasSubItems(item)) {
      const isOpen = openMenus[item.text] || false;

      return (
        <React.Fragment key={item.text}>
          <ListItemButton onClick={() => handleMenuClick(item.text)}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
            {isOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.subItems.map((subItem) => (
                <ListItemButton
                  key={subItem.text}
                  sx={{ pl: 4 }}
                  onClick={() => navigate(subItem.path)}
                  selected={location.pathname + location.search === subItem.path}
                >
                  <ListItemIcon>{subItem.icon}</ListItemIcon>
                  <ListItemText primary={subItem.text} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    return (
      <ListItemButton
        key={item.text}
        onClick={() => navigate(item.path)}
        selected={location.pathname + location.search === item.path}
      >
        <ListItemIcon>{item.icon}</ListItemIcon>
        <ListItemText primary={item.text} />
      </ListItemButton>
    );
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar />
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <List>
          {menuItems.map(renderMenuItem)}
        </List>
      </Box>
      <List>
        {/* <ListItemButton onClick={handleThemeToggle}>
          <ListItemIcon>
            {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </ListItemIcon>
          <ListItemText primary={theme.palette.mode === 'dark' ? "Light Mode" : "Dark Mode"} />
        </ListItemButton> */}
        <ListItemButton onClick={handleLogout}>
          <ListItemIcon><ExitToAppIcon /></ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${sidebarOpen ? drawerWidth : 0}px)` },
          ml: { sm: `${sidebarOpen ? drawerWidth : 0}px` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="toggle sidebar"
            edge="start"
            onClick={handleSidebarToggle}
            sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}
          >
            {sidebarOpen ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Ai11 Admin
          </Typography>

          <ApiEndpointSelector />

          {/* Theme toggle button */}
          <Tooltip title={theme.palette.mode === 'dark' ? "Switch to light mode" : "Switch to dark mode"}>
            <IconButton color="inherit" onClick={handleThemeToggle} sx={{ ml: 1 }}>
              {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Tooltip>
          
          {/* Logout button */}
          <Tooltip title="Logout">
            <IconButton color="inherit" onClick={handleLogout} sx={{ ml: 1 }}>
              <ExitToAppIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ 
          width: { sm: sidebarOpen ? drawerWidth : 0 }, 
          flexShrink: { sm: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              ...(sidebarOpen ? {} : { width: 0 }),
            },
          }}
          open={sidebarOpen}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${sidebarOpen ? drawerWidth : 0}px)` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;